// Pure helpers for the in-game help manual (modal + Help drawer). No Vue,
// no store: testable with plain node (see __tests__/render.test.mjs).
//
// The server compiles pages to HTML with two kinds of markers the client
// finishes off:
//   <i class="help-icon" data-icon="group/name" title="Name"></i>
//     → inline <svg> from the vue-svgicon registry (same drawing the rest of
//       the UI uses), so icons follow currentColor and need no sprite fetch.
//   <a href="/help/slug" class="help-ref" data-help="slug">label</a>
//     → left as-is; the surfaces intercept clicks on [data-help] and open
//       the page in place instead of leaving the game.

const ICON_RE = /<i class="help-icon" data-icon="([^"]+)" title="([^"]*)"><\/i>/g;

// vue-svgicon keeps the original colours as `_fill` / `_stroke` so it can
// restore them on demand; browsers ignore those attributes, so dropping
// them just makes the intent explicit: help icons take currentColor.
const ORIGINAL_COLOR_RE = / _(?:fill|stroke)="[^"]*"/g;

export function makeIconLookup(registry) {
  return (name) => (registry && registry[name]) || null;
}

// Help images (screenshots) are root-relative Phoenix static files:
// src="/img/help/...". The SPA can be served from another origin than
// Phoenix (Steam, a bare dev server), so surfaces pass `options.origin`
// and those srcs become absolute. Without an origin they are left alone.
const HELP_IMG_SRC_RE = /src="\/img\/help\//g;

// Building cards (catalog pages) pick their level with a radio group named
// after the building. The same page can be open in the modal and in the
// drawer at once, and two copies sharing one group would steal each other's
// selection, so every render gets its own group names.
const CARD_RADIO_NAME_RE = /name="(help-bcard-[^"]*)"/g;

// "The game mode you're viewing, Flash, doesn't have this patent. Switch to
// Legacy.": the in-game manual always follows the game's own mode, so the
// switch opens that page on the public site, in a new tab.
const SPEED_SWITCH_RE = /<a href="\/help\/([^"?#]+)" class="help-speed-switch" data-speed="(slow|medium|fast)">/g;
let renderCount = 0;

export function renderHelpHtml(html, lookupIcon, options = {}) {
  if (!html) return '';
  let out = html.replace(ICON_RE, (match, name, title) => {
    const icon = lookupIcon(name);
    if (!icon) return `<span class="help-icon help-icon-missing" title="${title}"></span>`;
    const body = String(icon.data).replace(ORIGINAL_COLOR_RE, '');
    return `<svg class="svg-icon help-icon" viewBox="${icon.viewBox}" role="img" aria-label="${title}"><title>${title}</title>${body}</svg>`;
  });
  const origin = options.origin ? String(options.origin).replace(/\/+$/, '') : '';
  if (origin) out = out.replace(HELP_IMG_SRC_RE, `src="${origin}/img/help/`);
  out = out.replace(SPEED_SWITCH_RE, (match, slug, speed) => (
    `<a href="${origin}/help/${slug}?speed=${speed}" class="help-speed-switch" data-speed="${speed}" target="_blank" rel="noopener">`
  ));
  if (out.includes('name="help-bcard-')) {
    renderCount += 1;
    out = out.replace(CARD_RADIO_NAME_RE, (match, name) => `name="${name}-r${renderCount}"`);
  }
  return out;
}

// Opens a rendered page at an anchor. A section anchor scrolls to its
// heading. A patent with levels has one page for all of them, and each
// level's anchor (`level-3`) is the id of that level's radio pip on the
// card: the level is selected and its card brought into view. Returns
// whether the anchor was found (the page may not be rendered yet).
export function applyHelpAnchor(root, anchor) {
  const target = root && anchor ? root.querySelector(`[id="${anchor}"]`) : null;
  if (!target) return false;
  if (target.type === 'radio') {
    target.checked = true;
    const card = target.closest('.help-bcard');
    if (card) card.scrollIntoView({ block: 'nearest' });
  } else {
    target.scrollIntoView({ block: 'start' });
  }
  return true;
}

// Title matches first, then term matches, then body text; stable by title.
export function searchPages(pages, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const rank = (p) => {
    if (p.title.toLowerCase().includes(q)) return 0;
    if ((p.terms || []).some((t) => t.toLowerCase().includes(q))) return 1;
    if ((p.text || '').toLowerCase().includes(q)) return 2;
    return -1;
  };
  return pages
    .map((p) => ({ p, r: rank(p) }))
    .filter(({ r }) => r >= 0)
    .sort((a, b) => a.r - b.r || a.p.title.localeCompare(b.p.title))
    .map(({ p }) => p);
}

// Public URL of a page, for the "copy link" button. `origin` is the site
// origin (config.BASE_URL in production, window.location.origin in dev).
export function publicHelpUrl(origin, slug, speed) {
  const base = `${origin || ''}/help/${slug}`;
  return speed && speed !== 'slow' ? `${base}?speed=${speed}` : base;
}
